import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Share as ShareIcon } from '@mui/icons-material';
import { gamoraApi } from '../services/api';
import Logo from './Logo';

const SharedWorkflowView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflowData, setWorkflowData] = useState<{
    workflow_id: string;
    workflow_name: string;
    created_at: string | null;
    steps: Array<{
      id: string;
      step_number: number;
      title: string;
      description: string;
      status: string;
    }>;
  } | null>(null);

  useEffect(() => {
    const loadSharedWorkflow = async () => {
      if (!shareToken) {
        setError('Share token is required');
        setLoading(false);
        return;
      }

      try {
        const data = await gamoraApi.getSharedWorkflow(shareToken);
        setWorkflowData(data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load shared workflow');
      } finally {
        setLoading(false);
      }
    };

    loadSharedWorkflow();
  }, [shareToken]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !workflowData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error || 'Shared workflow not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ bgcolor: '#fff', color: '#222', boxShadow: 'none', borderBottom: '1px solid #eee' }}>
        <Toolbar sx={{ minHeight: 64, px: 3 }}>
          <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Logo size={24} />
            <Typography variant="h6" component="div" sx={{ color: '#222', fontWeight: 600 }}>
              Gamura AI - Shared Workflow
            </Typography>
          </Box>
          <Button
            startIcon={<ShareIcon />}
            variant="outlined"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
            }}
          >
            Copy Link
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: '900px', mx: 'auto', p: 3 }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            {workflowData.workflow_name}
          </Typography>
          {workflowData.created_at && (
            <Typography variant="body2" sx={{ color: '#666' }}>
              Created: {new Date(workflowData.created_at).toLocaleString()}
            </Typography>
          )}
        </Paper>

        <Paper sx={{ p: 0 }}>
          {workflowData.steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <Box sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>
                  {step.step_number}. {step.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                  {step.description}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', mt: 1, display: 'block' }}>
                  Status: {step.status}
                </Typography>
              </Box>
              {index < workflowData.steps.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Paper>
      </Box>
    </Box>
  );
};

export default SharedWorkflowView;
